import React, { useContext } from "react";
import {
  Plan,
  BenefitItemProps,
  SupplementalBenefitCategoryTypeNames,
  PlanDetailsSection as PDS,
  PlanDetailsSection,
} from "../../../@types";
import {
  ItemIterator,
  PlanDetailSection,
  PlanDetailList,
  PlanDetailListWithHelpDrawer,
  BenefitItem,
  CollapsibleBox,
  OtherServicesBenefitItem,
  HelpDrawerWrapper,
  PlanDetailsHashLink,
} from "../../../components";
import {
  makeBenefitItemItems as mBII,
  useTranslate,
  planDataUtils,
} from "../../../helpers";
import {
  addProtocolToUrl,
  sendTealiumEvent,
} from "../../../helpers/objectUtilities";
import { MMPBenefitItem } from "./ExtraBenefits";
import { PreviewContext } from "../../../helpers";
import { hasOpioidTreatmentBenefit } from "../../../helpers/benefitHelpers";

const Benefits: React.FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const makeBenefitItemItems = (
    items: Array<{ label: string | JSX.Element; service?: string }>,
    benefit: string
  ): BenefitItemProps[] => mBII(items, benefit, plan);

  const t = useTranslate();
  const previewMode = useContext(PreviewContext);

  return (
    <>
      <PlanDetailSection title={t("plan_terms.benefits_and_costs")}>
        <PlanDetailList
          id={PDS.DOCTOR_SERVICES}
          title={t("plan_terms.doctor_services")}
          titleChildren={
            <a
              href={addProtocolToUrl(
                plan.provider_network_web_address || plan.url
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="ds-u-margin-left--1 preview-disable-exempt"
              onClick={(): void => {
                sendTealiumEvent(
                  "Plan Details",
                  "View Provider Network Directory"
                );
              }}
            >
              {t("plan_terms.view_provider_network_directory")}
            </a>
          }
        >
          <ItemIterator Component={BenefitItem}>
            {makeBenefitItemItems(
              [
                {
                  label: t("plan_terms.primary_doctor_visit"),
                  service: "primary",
                },
                {
                  label: t("plan_terms.specialist_visit"),
                  service: "specialist",
                },
              ],
              "doctor_visits"
            )}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList
          id={PDS.TESTS_LABS_IMAGING}
          title={t("plan_terms.tests_labs_imaging")}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: (
                      <CollapsibleBox
                        labelId="plan_terms.diagnostic_tests_and_procedures"
                        tealiumProps={{
                          event_action: "Plan Details",
                          event_label:
                            "Toggle - diagnostic tests and procedures",
                        }}
                        open={previewMode}
                      >
                        {t(
                          "plan_terms.diagnostic_tests_and_procedures.definition"
                        )}
                      </CollapsibleBox>
                    ),
                    service: "diagnostic_tests",
                  },
                  {
                    label: t("plan_terms.lab_services"),
                    service: "lab_services",
                  },
                  {
                    label: t("plan_terms.diagnostic_radiology_services"),
                    service: "diagnostic_radiology_services",
                  },
                  {
                    label: t("plan_terms.outpatient_xrays"),
                    service: "outpatient_xrays",
                  },
                ],
                "diagnostic_procedures"
              ),
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.emergency_care"),
                    service: "emergency",
                  },
                  {
                    label: t("plan_terms.urgent_care"),
                    service: "urgent_care",
                  },
                ],
                "emergency_care"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList
          id={PDS.HOSPITAL_SERVICES}
          title={t("plan_terms.hospital_services")}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.inpatient_hospital_coverage"),
                  },
                ],
                "inpatient_hospital"
              ),
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.outpatient_hospital_coverage"),
                  },
                ],
                "outpatient_hospital"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList
          id={PDS.SKILLED_NURSING}
          title={t("plan_terms.skilled_nursing")}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.skilled_nursing"),
                  },
                ],
                "skilled_nursing_facility"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailListWithHelpDrawer
          id={PlanDetailsSection.PREVENTIVE_SERVICES}
          titleId="plan_terms.preventive_services"
          helpContent={
            <>
              <p>{t("plan_terms.preventive_services.definition_extended")}</p>
              <p>
                <HelpDrawerWrapper
                  toggleText={t("plan_terms.preventive_services.learn_more")}
                  drawerTitle={t("plan_terms.preventive_services.learn_more")}
                >
                  <span className="ds-u-font-weight--semibold">
                    {t("plan_terms.preventive_services.pay_nothing")}
                  </span>
                  <ul>
                    <li>{t("plan_terms.preventive_services.flu_shots")}</li>
                    <li>{t("plan_terms.preventive_services.colonoscopies")}</li>
                    <li>
                      {t(
                        "plan_terms.preventive_services.cervical_cancer_screenings"
                      )}
                    </li>
                    <li>
                      {t(
                        "plan_terms.preventive_services.breast_cancer_screenings"
                      )}
                    </li>
                  </ul>
                  <span className="ds-u-font-weight--semibold">
                    {t("plan_terms.preventive_services.have_to_pay")}
                  </span>
                  <ul>
                    <li>
                      {t("plan_terms.preventive_services.glaucoma_tests")}
                    </li>
                    <li>
                      {t("plan_terms.preventive_services.diabetes_training")}
                    </li>
                    <li>{t("plan_terms.preventive_services.barium_enemas")}</li>
                    <li>
                      {t("plan_terms.preventive_services.ekg_screenings")}
                    </li>
                  </ul>
                  <p>
                    {t("plan_terms.preventive_services.costs_shown_disclaimer")}
                    <br />
                    <PlanDetailsHashLink
                      className="close-drawer-on-click"
                      label={t(
                        "plan_terms.preventive_services.contact_the_plan"
                      )}
                      plan={plan}
                      section={PlanDetailsSection.CONTACT_INFO}
                    />{" "}
                    {t("plan_terms.preventive_services.to_find_out_more")}
                  </p>
                </HelpDrawerWrapper>
              </p>
            </>
          }
          tealiumProps={{
            event_action: "Plan Details",
            event_label: "Toggle - preventive services",
          }}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.preventive_services"),
                  },
                ],
                "preventive_care"
              ),
            ]}
          </ItemIterator>
        </PlanDetailListWithHelpDrawer>

        <PlanDetailList id={PDS.AMBULANCE} title={t("plan_terms.ambulance")}>
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.ambulance_ground"),
                  },
                ],
                "ambulance"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList
          id={PDS.THERAPY_SERVICES}
          title={t("plan_terms.therapy_services")}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t(
                      "plan_terms.therapy_services.occupational_therapy"
                    ),
                    service: "occupational_therapy_visit",
                  },
                  {
                    label: t(
                      "plan_terms.therapy_services.physical_and_speech_therapy"
                    ),
                    service:
                      "physical_therapy_and_speech_and_language_therapy_visit",
                  },
                ],
                "rehabilitation_services"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList
          id={PDS.MENTAL_HEALTH_SERVICES}
          title={t("plan_terms.mental_health")}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.mental_health.outpatient_group_psy"),
                    service: "outpatient_group_therapy_visit_with_psychiatrist",
                  },
                  {
                    label: t("plan_terms.mental_health.outpatient_ind_psy"),
                    service:
                      "outpatient_individual_therapy_visit_with_psychiatrist",
                  },
                  {
                    label: t("plan_terms.mental_health.outpatient_group"),
                    service: "outpatient_group_therapy_visit",
                  },
                  {
                    label: t("plan_terms.mental_health.outpatient_ind"),
                    service: "outpatient_individual_therapy_visit",
                  },
                ],
                "mental_health"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        {hasOpioidTreatmentBenefit(plan) && (
          <PlanDetailList
            id={PDS.OPIOID_TREATMENT}
            title={t("more_benefits.opioid_treatment")}
          >
            <OtherServicesBenefitItem
              plan={plan}
              label={t("more_benefits.opioid_treatment")}
              category={
                SupplementalBenefitCategoryTypeNames.SB_CAT_OPIOID_TREATMENT_SERVICES
              }
            />
          </PlanDetailList>
        )}

        <PlanDetailList
          id={PDS.OTHER_SERVICES}
          title={t("plan_terms.other_services")}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.other_services.durable_equip"),
                    service: "durable_medical_equipment",
                  },
                  {
                    label: t("plan_terms.other_services.prosthetics"),
                    service: "prosthetics",
                  },
                ],
                "medical_equipment"
              ),
            ]}
          </ItemIterator>
          <OtherServicesBenefitItem
            plan={plan}
            label={t("plan_terms.other_services.dialysis")}
            category={
              SupplementalBenefitCategoryTypeNames.SB_CAT_DIALYSIS_SERVICES
            }
          />
          <BenefitItem
            plan={plan}
            benefit="medical_equipment"
            label={t("plan_terms.other_services.diabetes")}
            service="diabetes_supplies"
          />
        </PlanDetailList>

        {planDataUtils.showMMPBenefits(plan) && (
          <PlanDetailList
            id={PDS.MMP_HOME_HEALTH_SERVICES}
            title={t("plan_terms.mmp_home_health_services")}
          >
            {MMPBenefitItem(t, plan, {
              label: t(
                "plan_terms.mmp_home_health_services.additional_hours_of_care"
              ),
              category: "home_health_services",
              benefit: "additional_hours_of_care",
            })}
            {MMPBenefitItem(t, plan, {
              label: t(
                "plan_terms.mmp_home_health_services.personal_care_services"
              ),
              category: "home_health_services",
              benefit: "home_personal_care_services",
            })}
          </PlanDetailList>
        )}
      </PlanDetailSection>
    </>
  );
};

export default Benefits;
